import { jsx, jsxs } from "react/jsx-runtime";
import React__default, { useState, useEffect, useCallback, useRef } from "react";
import { ProductHighlightCardWidget } from "@brandclub/bc-widgets";
import { getAppConsistentParams } from "../../utils/appUtils.js";
import { getProductUrl } from "../../utils/productUtils.js";
import { decodeHTMLCharacters, formatMoney } from "../../utils/stringUtils.js";
import { useBrandAppConfig } from "../../api/cache/appConfigQueryOptions.js";
import { useProductClickTracking } from "../hooks/useProductClickTracking.js";
import { useConciergeContext } from "../ConciergeContextProvider.js";
import { fetchProductWithVariants } from "../api.js";
import { getHeadersWithUniqueVisitorId } from "../../Auth/index.js";
const getFontWeight = (weight) => {
  const weightMap = {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700
  };
  return weightMap[weight] || 500;
};
const formatReviewCount = (count) => {
  if (count >= 1e3) {
    const k = count / 1e3;
    return k % 1 === 0 ? `${k}K` : `${k.toFixed(1)}K`;
  }
  return count.toLocaleString();
};
const StarIcon = ({ color = "#ED792F" }) => /* @__PURE__ */ jsx(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "12",
    height: "12",
    viewBox: "0 0 16 16",
    fill: "none",
    style: { flexShrink: 0 },
    children: /* @__PURE__ */ jsx(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M15.4819 6.07562C15.4285 5.8977 15.2864 5.76804 15.1153 5.74117L10.3089 4.98558L8.1594 0.273902C8.0829 0.106169 7.92499 0 7.75211 0C7.5792 0 7.42132 0.106169 7.3448 0.273902L5.19518 4.98558L0.388901 5.74117C0.217838 5.76804 0.0756731 5.89771 0.0222526 6.07558C-0.0311937 6.2535 0.0133808 6.44878 0.137199 6.57932L3.61497 10.2469L2.7941 15.4256C2.76485 15.6099 2.83492 15.7962 2.97479 15.9062C3.05391 15.9684 3.14764 16 3.24182 16C3.31413 16 3.38668 15.9814 3.45312 15.9436L7.75208 13.4985L12.0509 15.9435C12.204 16.0306 12.3894 16.0161 12.5292 15.9061C12.6691 15.7962 12.7392 15.6099 12.71 15.4255L11.8888 10.2469L15.367 6.5793C15.4908 6.44879 15.5354 6.2535 15.4819 6.07562H15.4819Z",
        fill: color
      }
    )
  }
);
const ChevronIcon = ({ direction }) => /* @__PURE__ */ jsx("svg", { fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", style: { width: 14, height: 14, color: "#374151" }, children: direction === "left" ? /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2.5, d: "M15 19l-7-7 7-7" }) : /* @__PURE__ */ jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2.5, d: "M9 5l7 7-7 7" }) });
const CUSTOMER_FACING_KEYS = ["sizeName", "styleName", "colorName", "countName", "patternName", "weightName"];
const MAX_DIMENSIONS = 2;
const inferLabelFromValues = (values) => {
  const sample = values.join(" ").toLowerCase();
  if (/\blbs?\b|\bpounds?\b/.test(sample)) return "Weight";
  if (/\bcount\b/.test(sample)) return "Count";
  if (/\bdoses?\b|\bsupply\b/.test(sample)) return "Supply";
  if (/\btreatments?\b|\bapplications?\b/.test(sample)) return "Supply";
  if (/\b(small|medium|large|x-large|extra large)\b/.test(sample)) return "Size";
  if (/\bfl oz\b|\boz\b|\bml\b/.test(sample)) return "Size";
  if (/\bpack\b/.test(sample)) return "Pack";
  return null;
};
const GENERIC_KEYS = /* @__PURE__ */ new Set(["size", "style"]);
const formatDimensionLabel = (key, values) => {
  const base = key.replace(/Name$/, "").replace(/([A-Z])/g, " $1").trim().toLowerCase();
  if ((values == null ? void 0 : values.length) && GENERIC_KEYS.has(base)) {
    const inferred = inferLabelFromValues(values);
    if (inferred) return inferred;
  }
  return base.replace(/^\w/, (c) => c.toUpperCase());
};
const VariantCategoryRows = ({
  variants,
  parentSubCategoryId,
  currentVariationValues,
  currentStacklineSku,
  textColor,
  primaryColor,
  contrastColor,
  borderRadius,
  onVariantSelect
}) => {
  console.log("variants ", variants);
  const dimensions = React__default.useMemo(() => {
    const subCategoryFiltered = variants.filter((v) => !parentSubCategoryId || v.subCategoryId === parentSubCategoryId);
    const dimMap = /* @__PURE__ */ new Map();
    for (const v of subCategoryFiltered) {
      if (!v.variationNameValues) continue;
      for (const [key, val] of Object.entries(v.variationNameValues)) {
        if (!val || !CUSTOMER_FACING_KEYS.includes(key)) continue;
        if (!dimMap.has(key)) dimMap.set(key, /* @__PURE__ */ new Set());
        dimMap.get(key).add(val);
      }
    }
    return Array.from(dimMap.entries()).filter(([, values]) => values.size > 1).slice(0, MAX_DIMENSIONS).map(([key, values]) => {
      const valArr = Array.from(values);
      return {
        key,
        label: formatDimensionLabel(key, valArr),
        values: valArr.sort((a, b) => {
          const numA = parseFloat(a);
          const numB = parseFloat(b);
          if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
          return a.localeCompare(b, void 0, { numeric: true, sensitivity: "base" });
        })
      };
    });
  }, [variants, parentSubCategoryId]);
  if (dimensions.length === 0) return null;
  const isValueAvailable = (dimKey, val) => {
    return variants.some((v) => {
      if (!v.variationNameValues || v.variationNameValues[dimKey] !== val) return false;
      if (parentSubCategoryId && v.subCategoryId !== parentSubCategoryId) return false;
      if (v.isAvailable === false || v.isBrandClubReady === false) return false;
      if (v.retailPrice != null && v.retailPrice <= 0) return false;
      for (const dim of dimensions) {
        if (dim.key === dimKey) continue;
        const selectedVal = currentVariationValues == null ? void 0 : currentVariationValues[dim.key];
        if (selectedVal && v.variationNameValues[dim.key] !== selectedVal) return false;
      }
      return true;
    });
  };
  const findVariantForValue = (dimKey, value) => {
    return variants.find((v) => {
      if (!v.variationNameValues || v.variationNameValues[dimKey] !== value) return false;
      if (v.stacklineSku === currentStacklineSku) return false;
      if (v.isAvailable === false || v.isBrandClubReady === false) return false;
      if (v.retailPrice != null && v.retailPrice <= 0) return false;
      if (parentSubCategoryId && v.subCategoryId !== parentSubCategoryId) return false;
      for (const dim of dimensions) {
        if (dim.key === dimKey) continue;
        const selectedVal = currentVariationValues == null ? void 0 : currentVariationValues[dim.key];
        if (selectedVal && v.variationNameValues[dim.key] !== selectedVal) return false;
      }
      return true;
    });
  };
  const rowGap = 8;
  const approxRowHeight = 38;
  const minHeight = MAX_DIMENSIONS * approxRowHeight + (MAX_DIMENSIONS - 1) * rowGap;
  return /* @__PURE__ */ jsx("div", { style: { display: "flex", flexDirection: "column", gap: rowGap, minHeight }, children: dimensions.map((dim) => {
    const selectedValue = currentVariationValues == null ? void 0 : currentVariationValues[dim.key];
    return /* @__PURE__ */ jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 4 }, children: [
      /* @__PURE__ */ jsx(
        "span",
        {
          style: {
            fontSize: 8,
            fontWeight: 600,
            color: textColor,
            opacity: 0.5,
            textTransform: "uppercase",
            letterSpacing: 0.6
          },
          children: dim.label
        }
      ),
      /* @__PURE__ */ jsx("div", { className: "phc-variant-row", style: { display: "flex", gap: 6 }, children: dim.values.map((val) => {
        const isSelected = selectedValue === val;
        const available = isSelected || isValueAvailable(dim.key, val);
        const isDisabled = !available;
        const matchingVariant = !isSelected && available ? findVariantForValue(dim.key, val) : void 0;
        return /* @__PURE__ */ jsx(
          "span",
          {
            className: `phc-variant-pill${isSelected ? " phc-variant-selected" : ""}`,
            role: "button",
            tabIndex: isDisabled ? -1 : 0,
            "aria-disabled": isDisabled || void 0,
            onClick: () => {
              if (!isSelected && !isDisabled && matchingVariant) onVariantSelect(matchingVariant.stacklineSku);
            },
            onKeyDown: (e) => {
              if ((e.key === "Enter" || e.key === " ") && !isSelected && !isDisabled && matchingVariant)
                onVariantSelect(matchingVariant.stacklineSku);
            },
            style: {
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "3px 10px",
              fontSize: 11,
              fontWeight: 600,
              borderRadius,
              backgroundColor: isSelected ? primaryColor : "transparent",
              color: isSelected ? contrastColor : textColor,
              border: `1.5px solid ${isSelected ? primaryColor : isDisabled ? "#D1D5DB" : textColor}`,
              opacity: isDisabled ? 0.4 : 1,
              cursor: isSelected ? "default" : isDisabled ? "default" : "pointer",
              transition: "all 0.15s ease",
              whiteSpace: "nowrap"
            },
            children: val
          },
          `${dim.key}-${val}`
        );
      }) })
    ] }, dim.key);
  }) });
};
const imageGalleryStyles = `
  .phc-gallery-scroll::-webkit-scrollbar { display: none; }
  .phc-gallery-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%) scale(0.92);
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: rgba(255,255,255,0.95);
    border: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 10;
    box-shadow: 0 2px 8px rgba(0,0,0,0.10);
    opacity: 0;
    transition: opacity 0.2s ease, transform 0.2s ease, background 0.15s ease;
    pointer-events: none;
    backdrop-filter: blur(4px);
  }
  .phc-gallery:hover .phc-gallery-arrow {
    opacity: 1;
    pointer-events: auto;
    transform: translateY(-50%) scale(1);
  }
  .phc-gallery-arrow:hover { background: #f9fafb; box-shadow: 0 2px 12px rgba(0,0,0,0.14); }
`;
const ProductImageGallery = ({ images, title, borderRadius }) => {
  const scrollRef = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);
  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeft(el.scrollLeft > 10);
    setShowRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  }, []);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener("scroll", updateArrows);
    return () => el.removeEventListener("scroll", updateArrows);
  }, [updateArrows, images]);
  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = el.clientWidth;
    el.scrollTo({ left: el.scrollLeft + (dir === "right" ? amount : -amount), behavior: "smooth" });
  };
  if (images.length === 0) {
    return /* @__PURE__ */ jsx("div", { className: "phc-image", style: { aspectRatio: "1 / 1", borderRadius, backgroundColor: "#ffffff" } });
  }
  if (images.length === 1) {
    return /* @__PURE__ */ jsx(
      "div",
      {
        className: "phc-image",
        style: {
          aspectRatio: "1 / 1",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius,
          backgroundColor: "#ffffff",
          overflow: "hidden"
        },
        children: /* @__PURE__ */ jsx("img", { src: images[0], alt: title, style: { maxWidth: "100%", maxHeight: "100%", objectFit: "contain" } })
      }
    );
  }
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: "phc-image phc-gallery",
      style: { position: "relative", borderRadius, backgroundColor: "#ffffff", overflow: "hidden" },
      children: [
        /* @__PURE__ */ jsx("style", { dangerouslySetInnerHTML: { __html: imageGalleryStyles } }),
        showLeft && /* @__PURE__ */ jsx(
          "button",
          {
            className: "phc-gallery-arrow",
            style: { left: 6 },
            onClick: () => scroll("left"),
            "aria-label": "Previous image",
            children: /* @__PURE__ */ jsx(ChevronIcon, { direction: "left" })
          }
        ),
        showRight && /* @__PURE__ */ jsx(
          "button",
          {
            className: "phc-gallery-arrow",
            style: { right: 6 },
            onClick: () => scroll("right"),
            "aria-label": "Next image",
            children: /* @__PURE__ */ jsx(ChevronIcon, { direction: "right" })
          }
        ),
        /* @__PURE__ */ jsx(
          "div",
          {
            ref: scrollRef,
            className: "phc-gallery-scroll",
            style: {
              display: "flex",
              overflowX: "auto",
              scrollSnapType: "x mandatory",
              scrollbarWidth: "none"
            },
            children: images.map((url, idx) => /* @__PURE__ */ jsx(
              "div",
              {
                style: {
                  flexShrink: 0,
                  width: "100%",
                  aspectRatio: "1 / 1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  scrollSnapAlign: "start"
                },
                children: /* @__PURE__ */ jsx(
                  "img",
                  {
                    src: url,
                    alt: `${title} - ${idx + 1}`,
                    style: { maxWidth: "100%", maxHeight: "100%", objectFit: "contain" },
                    loading: idx === 0 ? "eager" : "lazy"
                  }
                )
              },
              idx
            ))
          }
        )
      ]
    }
  );
};
const responsiveStyles = `
  .phc-root { container-type: inline-size; }
  .phc-layout { display: flex; flex-direction: column; border: none !important; padding: 0 !important; }
  .phc-image { width: 100%; }
  .phc-title-link { text-decoration: none; }
  .phc-title-link:hover { text-decoration: none; }
  .phc-cta { transition: filter 0.2s ease !important; }
  .phc-cta:hover { filter: brightness(1.1); }
  .phc-variant-pill { transition: all 0.15s ease; }
  .phc-variant-pill:not(.phc-variant-selected):hover { background-color: rgba(0,0,0,0.05) !important; }
  .phc-variant-row { flex-wrap: nowrap; overflow-x: auto; scrollbar-width: none; }
  .phc-variant-row::-webkit-scrollbar { display: none; }
  @container (min-width: 600px) {
    .phc-layout {
      flex-direction: row;
      align-items: stretch;
      border: 1px solid var(--phc-border-color) !important;
      padding: var(--phc-padding) !important;
    }
    .phc-image { width: 260px; flex-shrink: 0; align-self: flex-start; }
    .phc-content { flex: 1; min-width: 0; display: flex; flex-direction: column; }
    .phc-variant-row { flex-wrap: wrap; overflow-x: visible; }
  }
`;
const SKELETON_BG = "#F3F4F6";
const SkeletonCard = ({ product, reason }) => {
  const hasRating = product ? product.reviewsRating > 0 || product.reviewsAverageRating > 0 : true;
  const hasReason = !!reason;
  return /* @__PURE__ */ jsxs("div", { className: "phc-root", style: { margin: "12px 0" }, children: [
    /* @__PURE__ */ jsx("style", { dangerouslySetInnerHTML: { __html: responsiveStyles } }),
    /* @__PURE__ */ jsxs(
      "div",
      {
        className: "phc-layout tw:animate-pulse",
        style: {
          "--phc-border-color": "#E5E7EB",
          "--phc-padding": "12px",
          gap: 16,
          borderRadius: 12,
          backgroundColor: "#fff",
          overflow: "hidden"
        },
        children: [
          /* @__PURE__ */ jsx("div", { className: "phc-image", style: { aspectRatio: "1 / 1", borderRadius: 8, backgroundColor: SKELETON_BG } }),
          /* @__PURE__ */ jsxs("div", { className: "phc-content", style: { display: "flex", flexDirection: "column", gap: 10, flex: 1 }, children: [
            /* @__PURE__ */ jsx("div", { style: { height: 44, width: "100%", backgroundColor: SKELETON_BG, borderRadius: 4 } }),
            hasRating && /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: 4 }, children: [
              /* @__PURE__ */ jsx("div", { style: { width: 12, height: 12, borderRadius: 3, backgroundColor: SKELETON_BG, flexShrink: 0 } }),
              /* @__PURE__ */ jsx("div", { style: { height: 12, width: 70, backgroundColor: SKELETON_BG, borderRadius: 4 } })
            ] }),
            /* @__PURE__ */ jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 8 }, children: [
              /* @__PURE__ */ jsxs("div", { style: { display: "flex", gap: 6 }, children: [
                /* @__PURE__ */ jsx("div", { style: { height: 26, width: 68, backgroundColor: SKELETON_BG, borderRadius: 9999 } }),
                /* @__PURE__ */ jsx("div", { style: { height: 26, width: 56, backgroundColor: SKELETON_BG, borderRadius: 9999 } }),
                /* @__PURE__ */ jsx("div", { style: { height: 26, width: 68, backgroundColor: SKELETON_BG, borderRadius: 9999 } })
              ] }),
              /* @__PURE__ */ jsxs("div", { style: { display: "flex", gap: 6 }, children: [
                /* @__PURE__ */ jsx("div", { style: { height: 26, width: 60, backgroundColor: SKELETON_BG, borderRadius: 9999 } }),
                /* @__PURE__ */ jsx("div", { style: { height: 26, width: 72, backgroundColor: SKELETON_BG, borderRadius: 9999 } })
              ] })
            ] }),
            /* @__PURE__ */ jsx("div", { style: { marginTop: "auto" }, children: /* @__PURE__ */ jsx("div", { style: { height: 34, width: 160, backgroundColor: SKELETON_BG, borderRadius: 9999 } }) }),
            hasReason && /* @__PURE__ */ jsx("div", { style: { height: 16, width: "65%", backgroundColor: SKELETON_BG, borderRadius: 4 } })
          ] })
        ]
      }
    )
  ] });
};
const ProductHighlightCard = ({ stacklineSku, reason }) => {
  const { trackProductClick } = useProductClickTracking();
  const { data: brandAppConfig } = useBrandAppConfig();
  const { apiBaseUrl } = useConciergeContext();
  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isVariantLoading, setIsVariantLoading] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const headers = await getHeadersWithUniqueVisitorId();
        const result = await fetchProductWithVariants({
          stacklineSku,
          headersToInject: headers,
          apiBaseUrl
        });
        if (!cancelled && result) setProduct(result);
      } catch {
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [stacklineSku, apiBaseUrl]);
  const urlSearchParams = getAppConsistentParams(window.location.search);
  const targetUrl = product ? getProductUrl({
    title: product.title,
    stacklineSku: product.stacklineSku,
    urlSearchParams,
    dtcProductPageUrl: product.dtcProductPageUrl,
    appConfigId: brandAppConfig == null ? void 0 : brandAppConfig.appConfigId
  }) : "#";
  const handleClick = useCallback(() => {
    if (!product) return;
    trackProductClick({
      stacklineSku: product.stacklineSku,
      productTitle: product.title,
      clickSource: "highlight",
      retailerId: product.retailerId,
      retailPrice: product.retailPrice,
      hasReward: product.hasRewards,
      rewardAmount: product.totalRewardAmount
    });
  }, [trackProductClick, product]);
  const handleVariantSelect = useCallback(
    (variantSku) => {
      if (!(product == null ? void 0 : product.variants)) return;
      const variant = product.variants.find((v) => v.stacklineSku === variantSku);
      if (!variant) return;
      setProduct((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          stacklineSku: variant.stacklineSku,
          title: variant.title,
          variationNameValues: variant.variationNameValues ?? prev.variationNameValues,
          ...variant.imageUrls && { imageUrls: variant.imageUrls },
          ...variant.retailPrice != null && { retailPrice: variant.retailPrice },
          ...variant.subCategoryId != null && { subCategoryId: variant.subCategoryId }
        };
      });
    },
    [product == null ? void 0 : product.variants]
  );
  if (isLoading) return /* @__PURE__ */ jsx(SkeletonCard, {});
  if (isVariantLoading) return /* @__PURE__ */ jsx(SkeletonCard, { product, reason });
  if (!product) return null;
  const reviewRating = product.reviewsRating > 0 ? Math.round(product.reviewsRating * 10) / 10 : product.reviewsAverageRating > 0 ? Math.round(product.reviewsAverageRating * 10) / 10 : 0;
  const reviewCount = product.reviewsCount || 0;
  return /* @__PURE__ */ jsx(ProductHighlightCardWidget, { widgetId: "product-highlight-card", displayName: "Product Highlight Card", children: (state, resolveColor) => /* @__PURE__ */ jsxs("div", { className: "phc-root", style: { margin: "12px 0" }, children: [
    /* @__PURE__ */ jsx("style", { dangerouslySetInnerHTML: { __html: responsiveStyles } }),
    /* @__PURE__ */ jsxs(
      "div",
      {
        className: "phc-layout",
        style: {
          "--phc-border-color": resolveColor(state.containerBorderColor),
          "--phc-padding": `${state.containerPadding}px`,
          gap: state.containerGap,
          borderRadius: state.containerBorderRadius,
          backgroundColor: resolveColor(state.containerBgColor),
          overflow: "hidden"
        },
        children: [
          /* @__PURE__ */ jsx(
            ProductImageGallery,
            {
              images: product.imageUrls || [],
              title: product.title,
              borderRadius: state.imageBorderRadius
            }
          ),
          /* @__PURE__ */ jsxs("div", { className: "phc-content", style: { display: "flex", flexDirection: "column", gap: 10 }, children: [
            /* @__PURE__ */ jsx(
              "a",
              {
                className: "phc-title-link",
                href: targetUrl,
                target: "_blank",
                rel: "noreferrer",
                onClick: handleClick,
                style: {
                  display: "-webkit-box",
                  fontSize: state.titleFontSize,
                  fontWeight: getFontWeight(state.titleFontWeight),
                  lineHeight: "1.4",
                  color: resolveColor(state.titleColor),
                  cursor: "pointer",
                  overflow: "hidden",
                  WebkitLineClamp: state.titleLineClamp,
                  WebkitBoxOrient: "vertical"
                },
                children: decodeHTMLCharacters(product.title)
              }
            ),
            reviewRating > 0 && /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: 4 }, children: [
              /* @__PURE__ */ jsx(StarIcon, { color: resolveColor(state.ratingStarColor) }),
              /* @__PURE__ */ jsxs(
                "span",
                {
                  style: {
                    fontSize: state.ratingFontSize,
                    fontWeight: 500,
                    color: resolveColor(state.ratingTextColor)
                  },
                  children: [
                    reviewRating,
                    " (",
                    formatReviewCount(reviewCount),
                    ")"
                  ]
                }
              )
            ] }),
            product.variants && product.variants.length > 1 && /* @__PURE__ */ jsx(
              VariantCategoryRows,
              {
                variants: product.variants,
                parentSubCategoryId: product.subCategoryId,
                currentVariationValues: product.variationNameValues,
                currentStacklineSku: product.stacklineSku,
                textColor: resolveColor(state.titleColor),
                primaryColor: resolveColor(state.ctaButtonBgColor),
                contrastColor: resolveColor(state.ctaButtonTextColor),
                borderRadius: state.ctaButtonBorderRadius,
                onVariantSelect: handleVariantSelect
              }
            ),
            /* @__PURE__ */ jsxs(
              "a",
              {
                className: "phc-cta",
                href: targetUrl,
                target: "_blank",
                rel: "noreferrer",
                onClick: handleClick,
                style: {
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  alignSelf: "flex-start",
                  marginTop: "auto",
                  gap: 12,
                  paddingLeft: 24,
                  paddingRight: 24,
                  height: state.ctaButtonHeight,
                  backgroundColor: resolveColor(state.ctaButtonBgColor),
                  color: resolveColor(state.ctaButtonTextColor),
                  borderRadius: state.ctaButtonBorderRadius,
                  fontSize: state.ctaButtonFontSize,
                  fontWeight: getFontWeight(state.ctaButtonFontWeight),
                  letterSpacing: 0.3,
                  textDecoration: "none",
                  cursor: "pointer"
                },
                children: [
                  /* @__PURE__ */ jsx("span", { children: "View product" }),
                  /* @__PURE__ */ jsx(
                    "span",
                    {
                      style: {
                        width: 1,
                        height: 14,
                        backgroundColor: resolveColor(state.ctaButtonTextColor),
                        opacity: 0.35
                      }
                    }
                  ),
                  /* @__PURE__ */ jsx("span", { style: { fontWeight: getFontWeight("bold") }, children: formatMoney(product.retailPrice) })
                ]
              }
            ),
            reason && /* @__PURE__ */ jsx(
              "span",
              {
                style: {
                  fontSize: state.reasonFontSize,
                  fontStyle: "italic",
                  lineHeight: "1.4",
                  color: resolveColor(state.reasonTextColor),
                  opacity: 0.7
                },
                children: reason
              }
            )
          ] })
        ]
      }
    )
  ] }) });
};
export {
  ProductHighlightCard
};
//# sourceMappingURL=ProductHighlightCard.js.map
